import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export type UserRole = "admin" | "customer";

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role: UserRole;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAdmin: false,
  login: async () => { throw new Error("not initialized"); },
  register: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  updatePassword: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const ADMIN_EMAIL = "ismanono@gmail.com";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Initial session check — fast
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session?.user) {
        const email = session.user.email ?? "";
        setUser({
          id: session.user.id,
          email,
          name: (session.user.user_metadata?.name as string) ||
                (session.user.user_metadata?.full_name as string) ||
                email.split("@")[0] || "",
          role: email.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase() ? "admin" : "customer",
        });
      }
      setLoading(false);
    }).catch(() => {
      if (mounted) setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_, session) => {
      if (!mounted) return;
      if (session?.user) {
        const email = session.user.email ?? "";
        setUser({
          id: session.user.id,
          email,
          name: (session.user.user_metadata?.name as string) ||
                (session.user.user_metadata?.full_name as string) ||
                email.split("@")[0] || "",
          role: email.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase() ? "admin" : "customer",
        });
      } else {
        setUser(null);
      }
      if (loading) setLoading(false);
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [loading]);

  // Background: ensure profile row exists + role is correct
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: existing } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();

      if (existing) {
        // If admin email, ensure admin role
        if (user.email.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase() && existing.role !== "admin") {
          await supabase.from("profiles").update({ role: "admin" }).eq("id", user.id);
        }
        // Update local role from DB if customer
        else if (existing.role && existing.role !== user.role) {
          setUser((prev) => prev ? { ...prev, role: existing.role as UserRole } : prev);
        }
      } else {
        // Create profile
        await supabase.from("profiles").insert({
          id: user.id,
          email: user.email,
          full_name: user.name || user.email.split("@")[0],
          role: user.role,
        });
      }
    })();
  }, [user?.id]);

  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
    if (!data.user) throw new Error("No user returned");

    const userEmail = data.user.email ?? email.trim();
    const authUser: AuthUser = {
      id: data.user.id,
      email: userEmail,
      name: (data.user.user_metadata?.name as string) ||
            (data.user.user_metadata?.full_name as string) ||
            userEmail.split("@")[0] || "",
      role: userEmail.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase() ? "admin" : "customer",
    };
    setUser(authUser);
    return authUser;
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
    });
    if (error) throw error;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/admin.html#/login?mode=update-password`,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: user?.role === "admin",
        login,
        register,
        logout,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
